/**
 * Currency formatting utilities using Intl.NumberFormat
 */

/**
 * Format amount with proper currency symbol and locale
 * @param {number} amount - The amount to format
 * @param {string} currencyCode - ISO 4217 currency code (e.g., 'USD', 'GBP', 'NGN')
 * @param {string} locale - Optional locale (defaults to 'en-US')
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount, currencyCode = 'GBP', locale = 'en-US') {
  // Handle invalid amounts
  if (typeof amount !== 'number' || isNaN(amount)) {
    amount = 0;
  }

  // FIX: Round to 2 decimal places BEFORE formatting to fix 1/1024 floating-point bug
  amount = Math.round(amount * 100) / 100;

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    console.error('Error formatting currency:', error);
    // Fallback to simple formatting
    return `${getCurrencySymbol(currencyCode)}${amount.toFixed(2)}`;
  }
}

/**
 * Get currency symbol from currency code
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {string} Currency symbol
 */
export function getCurrencySymbol(currencyCode) {
  const symbols = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    NGN: '₦',
    KES: 'KSh',
    ZAR: 'R',
    CAD: 'C$',
    AUD: 'A$',
    INR: '₹',
    CNY: '¥',
    JPY: '¥',
    GHS: '₵',
    TZS: 'TSh',
    AED: 'د.إ',
    CHF: 'Fr',
  };

  return symbols[currencyCode] || currencyCode;
}

/**
 * Get recommended locale for currency code
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {string} Locale string
 */
export function getCurrencyLocale(currencyCode) {
  const locales = {
    USD: 'en-US',
    EUR: 'de-DE',
    GBP: 'en-GB',
    NGN: 'en-NG',
    KES: 'en-KE',
    ZAR: 'en-ZA',
    CAD: 'en-CA',
    AUD: 'en-AU',
    INR: 'en-IN',
    CNY: 'zh-CN',
    JPY: 'ja-JP',
    GHS: 'en-GH',
    TZS: 'en-TZ',
    AED: 'ar-AE',
    CHF: 'de-CH',
  };

  return locales[currencyCode] || 'en-US';
}

/**
 * Format currency with locale awareness
 * @param {number} amount - The amount to format
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {string} Formatted currency string with proper locale
 */
export function formatCurrencyWithLocale(amount, currencyCode = 'GBP') {
  const locale = getCurrencyLocale(currencyCode);
  return formatCurrency(amount, currencyCode, locale);
}

/**
 * Parse currency string to number
 * @param {string} currencyString - String with currency symbols
 * @returns {number} Numeric value
 */
export function parseCurrency(currencyString) {
  if (typeof currencyString === 'number') return currencyString;
  
  // Remove all non-numeric characters except decimal point and minus
  const cleaned = currencyString.replace(/[^\d.-]/g, '');
  const parsed = parseFloat(cleaned);
  
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Get all supported currencies with metadata
 * @returns {Array} Array of currency objects
 */
export function getSupportedCurrencies() {
  return [
    { code: 'USD', name: 'US Dollar', symbol: '$', region: 'United States' },
    { code: 'EUR', name: 'Euro', symbol: '€', region: 'Eurozone' },
    { code: 'GBP', name: 'British Pound Sterling', symbol: '£', region: 'United Kingdom' },
    { code: 'NGN', name: 'Nigerian Naira', symbol: '₦', region: 'Nigeria' },
    { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh', region: 'Kenya' },
    { code: 'ZAR', name: 'South African Rand', symbol: 'R', region: 'South Africa' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', region: 'Canada' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', region: 'Australia' },
    { code: 'INR', name: 'Indian Rupee', symbol: '₹', region: 'India' },
    { code: 'CNY', name: 'Chinese Yuan Renminbi', symbol: '¥', region: 'China' },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥', region: 'Japan' },
    { code: 'GHS', name: 'Ghanaian Cedi', symbol: '₵', region: 'Ghana' },
    { code: 'TZS', name: 'Tanzanian Shilling', symbol: 'TSh', region: 'Tanzania' },
    { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', region: 'UAE' },
    { code: 'CHF', name: 'Swiss Franc', symbol: 'Fr', region: 'Switzerland' },
  ];
}

/**
 * Detect user's currency based on browser locale
 * @returns {string} Currency code
 */
export function detectUserCurrency() {
  try {
    const locale = navigator.language || 'en-US';
    const localeToCurrency = {
      'en-US': 'USD',
      'en-GB': 'GBP',
      'en-NG': 'NGN',
      'en-KE': 'KES',
      'en-ZA': 'ZAR',
      'en-CA': 'CAD',
      'en-AU': 'AUD',
      'en-IN': 'INR',
      'zh-CN': 'CNY',
      'ja-JP': 'JPY',
      'en-GH': 'GHS',
      'en-TZ': 'TZS',
      'ar-AE': 'AED',
      'de-CH': 'CHF',
      'de-DE': 'EUR',
      'fr-FR': 'EUR',
      'es-ES': 'EUR',
      'it-IT': 'EUR',
    };

    return localeToCurrency[locale] || 'GBP';
  } catch (error) {
    console.error('Error detecting currency:', error);
    return 'GBP';
  }
}

/**
 * Format compact currency (K, M, B notation)
 * @param {number} amount - The amount to format
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {string} Compact formatted currency string
 */
export function formatCurrencyCompact(amount, currencyCode = 'GBP') {
  const symbol = getCurrencySymbol(currencyCode);
  
  if (amount >= 1000000000) {
    return `${symbol}${(amount / 1000000000).toFixed(1)}B`;
  } else if (amount >= 1000000) {
    return `${symbol}${(amount / 1000000).toFixed(1)}M`;
  } else if (amount >= 1000) {
    return `${symbol}${(amount / 1000).toFixed(1)}K`;
  }
  
  return `${symbol}${amount.toFixed(2)}`;
}
